import { makeStyles } from '@material-ui/core/styles'

const BRAND_NAVY = '#001C57'
const CARD_HEADER_BACKGROUND = '#E0EAFF'
const HEADER_IMAGE_HEIGHT = 120
const HEADER_ICON_SIZE = 56
const HEADER_ICON_HALF = HEADER_ICON_SIZE / 2

export const useRoleCardStyles = makeStyles(theme => ({
  card: {
    borderRadius: 16,
    border: '1px solid #E0E0E0',
    boxShadow: '0 8px 24px rgba(0, 28, 87, 0.06)',
    boxSizing: 'border-box',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    overflow: 'hidden',
    textDecoration: 'none',
    color: 'inherit',
    cursor: 'pointer',
    '&:hover, &:focus, &:active, &:visited': {
      textDecoration: 'none',
      color: 'inherit',
    },
    '& *': {
      textDecoration: 'none',
    },
    transition: theme.transitions.create(['box-shadow', 'border-color'], {
      duration: 200,
    }),
    '&:hover': {
      borderColor: '#C5D4FF',
      boxShadow: '0 12px 32px rgba(0, 28, 87, 0.1)',
      '& $continueButton': {
        backgroundColor: '#E0E8EC',
      },
      '& $continueButton .MuiSvgIcon-root': {
        transform: 'translateX(2px)',
      },
    },
    '&:focus-visible': {
      outline: '2px solid #0053FF',
      outlineOffset: 2,
    },
  },
  header: {
    backgroundColor: CARD_HEADER_BACKGROUND,
    padding: theme.spacing(3, 3, 2.5),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  headerShell: {
    position: 'relative',
  },
  headerImageArea: {
    height: HEADER_IMAGE_HEIGHT,
    backgroundColor: CARD_HEADER_BACKGROUND,
    backgroundSize: 'cover',
    backgroundPosition: 'center 25%',
    backgroundRepeat: 'no-repeat',
    flexShrink: 0,
  },
  iconTileOverlay: {
    position: 'absolute',
    top: HEADER_IMAGE_HEIGHT,
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: HEADER_ICON_SIZE,
    height: HEADER_ICON_SIZE,
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 12px rgba(0, 28, 87, 0.08)',
    color: BRAND_NAVY,
    zIndex: 2,
    '& .MuiSvgIcon-root': {
      fontSize: 28,
    },
  },
  headerMeta: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: theme.spacing(0, 2.5, 1.5),
    paddingTop: HEADER_ICON_HALF + theme.spacing(1.5),
    backgroundColor: '#FFFFFF',
    position: 'relative',
    zIndex: 1,
    '& $audience': {
      marginBottom: theme.spacing(0.5),
    },
    '& $roleTitle': {
      fontSize: '1.75rem',
    },
  },
  iconTile: {
    width: 72,
    height: 72,
    borderRadius: 16,
    backgroundColor: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
    boxShadow: '0 4px 12px rgba(0, 28, 87, 0.08)',
    color: BRAND_NAVY,
    position: 'relative',
    zIndex: 1,
    '& .MuiSvgIcon-root': {
      fontSize: 36,
    },
    '& img': {
      display: 'block',
      maxWidth: 48,
      maxHeight: 48,
      width: 'auto',
      height: 'auto',
      objectFit: 'contain',
    },
  },
  audience: {
    color: '#0053FF',
    fontSize: '0.6875rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    lineHeight: 1.4,
    textTransform: 'uppercase',
    marginBottom: theme.spacing(0.75),
    position: 'relative',
    zIndex: 1,
  },
  roleTitle: {
    fontFamily: '"Source Serif Pro", serif',
    fontWeight: 700,
    fontSize: '2rem',
    lineHeight: 1.15,
    color: BRAND_NAVY,
    margin: 0,
    position: 'relative',
    zIndex: 1,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.75rem',
    },
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    alignItems: 'stretch',
    padding: theme.spacing(3),
    paddingTop: theme.spacing(2.5),
  },
  bodyWithHeaderImage: {
    paddingTop: theme.spacing(1.5),
  },
  intro: {
    color: '#425D69',
    fontSize: '0.9375rem',
    lineHeight: 1.6,
    marginBottom: theme.spacing(2.5),
    textAlign: 'left',
    width: '100%',
  },
  featureList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.75),
    margin: 0,
    marginBottom: theme.spacing(2.5),
    padding: 0,
    width: '100%',
    listStyle: 'none',
  },
  featureRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
  },
  featureIconWrap: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    backgroundColor: '#CCDDFF',
    color: '#001C57',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& .MuiSvgIcon-root': {
      fontSize: 18,
    },
  },
  featureText: {
    color: '#425D69',
    fontSize: '0.875rem',
    lineHeight: 1.5,
    margin: 0,
    paddingTop: 4,
  },
  continueButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.75),
    width: '100%',
    minHeight: 40,
    marginTop: 'auto',
    padding: theme.spacing(1, 2.5),
    borderRadius: 9999,
    backgroundColor: '#EBF0F2',
    color: '#172024',
    boxSizing: 'border-box',
    flexWrap: 'nowrap',
    transition: theme.transitions.create(['background-color'], {
      duration: 200,
    }),
    '& .MuiSvgIcon-root': {
      fontSize: 18,
      flexShrink: 0,
      transition: theme.transitions.create(['transform'], { duration: 200 }),
    },
  },
  continueLabel: {
    color: '#172024',
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.4,
    whiteSpace: 'nowrap',
  },
}))
